import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  User,
  Bell,
  HelpCircle,
  LogOut,
  ChevronDown,
  MessageSquare,
  CreditCard,
  Plug,
  Users,
  LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NewUserModal from '../components/NewUserModal';
import ProfilePhoto from '../components/ProfilePhoto';
import Tooltip from '../components/Tooltip';
import { getHomeRoute, setHomeRoute } from '../utils/homeRoute';

const PortalLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user, isNewUser, setIsNewUser, isAccountManager } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [currentHome, setCurrentHome] = useState<'/dashboard' | '/chat'>(getHomeRoute());

  // Mock notifications - replace with actual notification data
  const notifications: Array<{ id: string; title: string; message: string; time: string; read: boolean }> = [];

  useEffect(() => {
    if (isNewUser) {
      setShowNewUserModal(true);
    }
  }, [isNewUser]);

  const handleHomeToggle = (route: '/dashboard' | '/chat') => {
    setHomeRoute(route);
    setCurrentHome(route);
    navigate(route);
  };

  const baseNavItems = [
    { name: 'Dashboard', icon: LayoutDashboard, route: '/dashboard' },
    { name: 'My Niwt', icon: MessageSquare, route: '/chat' },
    { name: 'Niwt Directory', icon: Users, route: '/directory' },
    { name: 'Integrations', icon: Plug, route: '/integrations' },
  ];

  // Add Billing for Account Managers
  const navItems = [
    ...baseNavItems,
    ...(isAccountManager ? [{ name: 'Billing', icon: CreditCard, route: '/billing' }] : []),
  ];

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo and Navigation */}
            <div className="flex items-center space-x-10">
              {/* Logo */}
              <Link to="/chat" className="flex-shrink-0">
                <img
                  src="/images/niwt-logo-horizontal.svg"
                  alt="Niwt"
                  width="140"
                  height="48"
                  className="hidden sm:block"
                />
                <img
                  src="/images/niwt-logo.png"
                  alt="Niwt"
                  width="48"
                  height="48"
                  className="block sm:hidden"
                />
              </Link>

              {/* Navigation */}
              <nav className="hidden md:flex space-x-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.route;
                  return (
                    <Link
                      key={item.route}
                      to={item.route}
                      className={`flex items-center px-5 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        isActive
                          ? 'bg-purple-50 text-niwt-primary'
                          : 'text-niwt-text hover:bg-gray-100 hover:text-niwt-primary'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Right side - User menu */}
            <div className="flex items-center space-x-4">
              {/* Home toggle */}
              <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-full p-0.5">
                <button
                  onClick={() => handleHomeToggle('/dashboard')}
                  className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                    currentHome === '/dashboard'
                      ? 'bg-niwt-primary text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => handleHomeToggle('/chat')}
                  className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                    currentHome === '/chat'
                      ? 'bg-niwt-primary text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  My Niwt
                </button>
              </div>

              {/* Notifications */}
              <div className="relative">
                <Tooltip text="Notifications" position="bottom">
                  <button
                    onClick={() => setNotificationMenuOpen(!notificationMenuOpen)}
                    className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Bell className="w-5 h-5" />
                    {notifications.some(n => !n.read) && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </button>
                </Tooltip>

                {/* Notification Dropdown */}
                {notificationMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setNotificationMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                      <div className="p-4 border-b border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="p-8 text-center">
                            <Bell className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                            <p className="text-sm text-gray-500">No notifications yet</p>
                            <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notification) => (
                              <div
                                key={notification.id}
                                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  !notification.read ? 'bg-purple-50/50' : ''
                                }`}
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                    <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                                    <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                                  </div>
                                  {!notification.read && (
                                    <span className="w-2 h-2 bg-niwt-primary rounded-full mt-1"></span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {notifications.length > 0 && (
                        <div className="p-3 border-t border-gray-200">
                          <button className="w-full text-center text-sm text-niwt-primary hover:text-niwt-secondary font-medium transition-colors">
                            Mark all as read
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Help */}
              <Tooltip text="Help & Support" position="bottom">
                <Link
                  to="/help"
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors inline-block"
                >
                  <HelpCircle className="w-5 h-5" />
                </Link>
              </Tooltip>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ProfilePhoto
                    userName={`${user?.given_name} ${user?.family_name}`}
                    userEmail={user?.email}
                    size="small"
                  />
                  <div className="hidden md:block text-left max-w-[200px]">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.given_name} {user?.family_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                      <div className="p-3 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.given_name} {user?.family_name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 inline mr-2" />
                        Profile Settings
                      </Link>
                      {isAccountManager && (
                        <Link
                          to="/billing"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <CreditCard className="w-4 h-4 inline mr-2" />
                          Billing & Subscription
                        </Link>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 border-t border-gray-200"
                      >
                        <LogOut className="w-4 h-4 inline mr-2" />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${location.pathname === '/chat' ? 'py-4' : 'py-8 min-h-[calc(100vh-200px)]'}`}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-niwt-text text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main footer content */}
          <div className="py-12 lg:py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Company info and logo */}
              <div className="sm:col-span-2 lg:col-span-1">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src="/images/niwt-logo-horizontal.svg"
                    alt="Niwt"
                    width="120"
                    height="40"
                    className="brightness-0 invert"
                  />
                </Link>
                <p className="text-gray-400 text-sm leading-relaxed">
                  AI twins that preserve your team's knowledge and ensure business continuity.
                </p>
              </div>

              {/* Links sections */}
              <div>
                <h3 className="text-white font-semibold mb-4">Product</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/chat" className="text-gray-400 hover:text-white transition-colors text-sm">
                      My Niwt
                    </Link>
                  </li>
                  <li>
                    <Link to="/directory" className="text-gray-400 hover:text-white transition-colors text-sm">
                      Niwt Directory
                    </Link>
                  </li>
                  <li>
                    <Link to="/integrations" className="text-gray-400 hover:text-white transition-colors text-sm">
                      Integrations
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-4">Company</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="mailto:hello@niwt.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-4">Legal</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="https://www.niwt.com/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">
                      Privacy
                    </a>
                  </li>
                  <li>
                    <a href="https://www.niwt.com/terms" className="text-gray-400 hover:text-white transition-colors text-sm">
                      Terms
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 py-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Niwt, LLC. All rights reserved.
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>SOC 2 Type II</span>
                <span>•</span>
                <span>CMMC-ready</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* New User Modal */}
      <NewUserModal 
        isOpen={showNewUserModal} 
        onClose={() => setShowNewUserModal(false)} 
      />
    </div>
  );
};

export default PortalLayout;